<template>
    <div>
        <div class="uploader text-white"
             @dragenter.prevent="onDragEnter"
             @dragleave.prevent="onDragLeave"
             @dragover.prevent
             @drop.prevent="onDrop"
             @click="select"
             :class="[isDragging ? 'bg-secondary' : 'bg-primary']">
            <i class="fas fa-cloud-upload-alt" style="font-size: 85px;"></i>
            <p> Drag your file here </p>
            <div> OR</div>
            <button class="btn btn-default btn-lg text-primary btn-block"> Select a File</button>
            <div class="file-input" hidden>
                <label class="label-cover text-primary" for="file">Select a File</label>
                <input class="label-cover hidden-input-btn" type="file" id="file"
                       ref="input"
                       :disabled="multiple && max_length_reached"
                       @change="onInputChange($event.target.files)"
                       :multiple="multiple"
                       :accept="accept"
                       hidden>
            </div>
            <p class="help-block text-danger mt10" v-if="max_length_reached">
                Max of {{ maxLength }} files reached
            </p>
        </div>
        <div class="mt-3" v-if="files.length">
            <div class="mb-3" v-for="file in files" :key="file.name">
                <div class="mb-1 text-left">
              <span class="text-danger mr10" title="Remove" v-if="!hasStatus(file, 'uploading')" @click="remove(file)"
                    style="cursor: pointer">
                  <i class="fa fa-minus-circle mr-2"></i>
              </span>
                    {{ file.name }} - {{ humanFileSize(file.size) }}
                    <span class="pull-right">
                  <small class="text-danger" v-if="hasStatus(file, 'invalid') || hasStatus(file, 'error')">
                    {{ file.error ? file.error : '' }}
                  </small>
                  <a class="btn btn-xs btn-outline-danger" href="#" v-if="hasStatus(file, 'uploading')"
                     @click.prevent.stop="cancelUpload(file)">
                     CANCEL
                  </a>
                  <a class="btn btn-xs btn-outline-warning" href="#" v-if="hasStatus(file, 'canceled')"
                     @click.prevent.stop="retry(file)">
                     RETRY
                  </a>
                  <a class="btn btn-xs btn-outline-primary" href="#" v-if="hasStatus(file, 'queued')"
                     @click.prevent.stop="upload(file)">
                     UPLOAD
                  </a>
                  <small class="text-success" v-if="hasStatus(file, 'uploaded')">
                      UPLOADED
                      <i class="fa fa-check ml-1"></i>
                  </small>
                  <small class="text-info" v-if="hasStatus(file, 'processing')">
                      PROCESSING
                      <i class="fa fa-spin fa-spinner ml-1"></i>
                  </small>
              </span>
                    <div class="clearfix">
                    </div>
                </div>
                <progress-bar
                        :value="file.percent"
                        :type="progressType(file)"
                        :animated="hasStatus(file, 'uploading')"
                        style="height: 4px;">
                </progress-bar>
            </div>
            <div class="pull-right">
                <button class="btn btn-xs btn-default ml-1" v-if="!has_at_least_one_uploading && this.files.length > 1"
                        @click="clearAll">
                    <i class="fa fa-trash mr-1"></i>
                    CLEAR QUEUE
                </button>
                <button class="btn btn-xs btn-danger ml-1" v-if="has_at_least_two_uploading" @click="cancelAll">
                    <i class="fa fa-ban mr-1"></i>
                    CANCEL ALL
                </button>
                <button class="btn btn-xs btn-warning ml-1" v-if="has_at_least_two_canceled" @click="retryAll">
                    <i class="fa fa-refresh mr-1"></i>
                    RETRY ALL
                </button>
                <button class="btn btn-xs btn-primary ml-1" v-if="has_at_least_two_queued" @click="uploadAll">
                    <i class="fa fa-upload mr-1"></i>
                    UPLOAD ALL
                </button>
            </div>
            <div class="clearfix">
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            multiple: {default: false},
            url: {required: true},
            maxLength: {default: 10},
            maxFileSize: {default: 5}, // In MB
            httpMethod: {default: 'post'},
            accept: {default: '*'},
            fieldName: {default: 'file'},
            uploadOnSelect: {default: false},
            buttonClass: {default: 'btn-default btn-block'},
        },

        data: () => ({
            isDragging: false,
            dragCount: 0,
            files: [],
        }),

        computed: {
            select_button_text() {
                if (this.files.length === 0) return this.multiple ? `Select files` : 'Select File';
                if (this.files.length === 1) return '1 file selected';
                return `${this.files.length}/${this.maxLength} files selected`;
            },
            canceled_files() {
                return this.files.filter(file => this.hasStatus(file, 'canceled'));
            },
            queued_files() {
                return this.files.filter(file => this.hasStatus(file, 'queued'));
            },
            uploading_files() {
                return this.files.filter(file => this.hasStatus(file, 'uploading') || this.hasStatus(file, 'processing'));
            },
            upload_button() {
                return this.files.length > 1 ? `Upload ${this.files.length} files` : 'Upload file';
            },
            has_file() {
                return this.files.length > 0;
            },
            has_at_least_two_queued() {
                return this.queued_files.length > 1;
            },
            has_at_least_two_canceled() {
                return this.canceled_files.length > 1;
            },
            has_at_least_two_uploading() {
                return this.uploading_files.length > 1;
            },
            has_at_least_one_uploading() {
                return this.uploading_files.length >= 1;
            },
            max_length_reached() {
                return this.files.length >= this.maxLength;
            },
        },
        methods: {
            hasStatus(file, status) {
                return file.status === status;
            },

            progressType(file) {
                switch (file.status) {
                case 'canceled':
                    return 'warning';
                case 'uploaded':
                    return 'success';
                case 'error':
                    return 'danger';

                default:
                    return 'primary';
                }
            },

            select() {
                this.$refs.input.value = null;
                this.$refs.input.click();
            },

            onDrop(e) {
                e.stopPropagation();
                var files = e.dataTransfer.files;
                this.isDragging = false;
                this.dragCount = 0;
                this.onInputChange(files);
            },
            onDragEnter(e) {
                this.dragCount++;
                this.isDragging = true;
            },
            onDragLeave(e) {
                this.dragCount--;
                if (this.dragCount <= 0) {
                    this.isDragging = false;
                }
            },
            onInputChange(files) {
                if (!this.multiple && files.length) this.files = [];
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    if (this.exists(file)) continue;
                    if (this.max_length_reached) break;

                    this.queue(file);
                }

                if (this.uploadOnSelect) this.uploadAll();
            },

            exists(file) {
                return this.files.some(f => f.name === file.name);
            },

            queue(file) {
                this.files.push({
                    name: file.name,
                    obj: file,
                    loaded: 0,
                    percent: 0,
                    size: file.size,
                    status: this.invalidSize(file.size) ? 'invalid' : 'queued',
                    source: window.axios.CancelToken.source(),
                    error: this.invalidSize(file.size) ? `Invalid file size. Max of ${this.maxFileSize} MB` : null,
                });
            },

            invalidSize(size) {
                return (this.maxFileSize * 1000000) < size;
            },

            humanFileSize(bytes, si = true) {
                var thresh = si ? 1000 : 1024;
                if (Math.abs(bytes) < thresh) {
                    return bytes + ' B';
                }
                var units = si
                    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
                    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
                var u = -1;
                do {
                    bytes /= thresh;
                    ++u;
                } while (Math.abs(bytes) >= thresh && u < units.length - 1);
                return bytes.toFixed(1) + ' ' + units[u];
            },

            remove(file) {
                this.files = this.files.filter(f => f.name !== file.name);
            },

            upload(file) {
                const formData = new FormData();
                formData.append(this.fieldName, file.obj, file.name);
                file.status = 'uploading';
                return window.axios[this.httpMethod](this.url, formData, {
                    cancelToken: file.source.token,
                    onUploadProgress: this.onUploadProgress.bind(this, file)
                })
                    .then(this.onUploadSuccess.bind(this, file))
                    .catch(this.onUploadError.bind(this, file));
            },

            retry(file) {
                file.source = window.axios.CancelToken.source();
                file.loaded = 0;
                this.upload(file);
            },

            retryAll() {
                this.canceled_files.forEach(file => this.retry(file));
            },

            uploadAll() {
                this.queued_files.forEach(file => this.upload(file));
            },

            clearAll() {
                this.files = [];
                this.$refs.input.value = '';
            },

            cancelAll() {
                this.uploading_files.forEach(file => this.cancelUpload(file));
            },

            onUploadProgress(file, progressEvent) {
                file.percent = this.percent(progressEvent.loaded, progressEvent.total);
                file.loaded = progressEvent.loaded;
                if (file.percent === 100) file.status = 'processing';
            },

            onUploadSuccess(file, response) {
                file.status = 'uploaded';
                this.$emit('uploaded', response);
                return response;
            },

            onUploadError(file, err) {
                file.status = 'error';
                if (window.axios.isCancel(err)) {
                    file.error = err.message;
                    file.status = 'canceled';
                }

                const {response} = err;
                if (response && response.status === 422) {
                    file.error = response.data.errors[this.fieldName][0];
                }

                this.$emit('failed', err.response, file);
                console.error('UPLOAD FAILED. File:', file, err.response);
            },

            cancelUpload(file) {
                file.source.cancel('Canceled');
                file.status = 'canceled';
            },

            percent(current, total) {
                return Math.round((current * 100) / total);
            }
        }
    };
</script>

<style lang="css">
    .uploader {
        width: 100%;
        padding: 40px 15px;
        text-align: center;
        border-radius: 10px;
        font-size: 20px;
        position: relative;
    }

    .file-input {
        width: 200px;
        margin: auto;
        height: 68px;
        position: relative;
    }

    .label-cover {
        background: #fff;
        width: 100%;
        position: absolute;
        left: 0;
        top: 0;
        padding: 10px;
        border-radius: 4px;
        margin-top: 7px;
        cursor: pointer;
    }

    .hidden-input-btn {
        opacity: 0;
        z-index: -1;
    }

</style>
